import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  Unsubscribe,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import {
  ClassroomTeam,
  ClassroomTeamColorKey,
  nextTeamColorKey,
} from './types';

function teamsCol(squadId: string) {
  return collection(db, 'squads', squadId, 'teams');
}

function teamRef(squadId: string, teamId: string) {
  return doc(db, 'squads', squadId, 'teams', teamId);
}

export class ClassroomTeamsService {
  static subscribe(
    squadId: string,
    onData: (teams: ClassroomTeam[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const q = query(teamsCol(squadId), orderBy('createdAt', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const teams = snap.docs.map((d) => {
          const data = d.data() as Omit<ClassroomTeam, 'id'>;
          return { ...data, id: d.id, squadId } as ClassroomTeam;
        });
        onData(teams);
      },
      (err) => onError?.(err as Error)
    );
  }

  static async createTeam(args: {
    squadId: string;
    name: string;
    memberIds?: string[];
    colorKey?: ClassroomTeamColorKey;
    existingTeams: ClassroomTeam[];
    createdBy?: string;
  }): Promise<string> {
    const ref = doc(teamsCol(args.squadId));
    const colorKey = args.colorKey || nextTeamColorKey(args.existingTeams);
    const now = Date.now();
    const payload: Omit<ClassroomTeam, 'id'> = {
      squadId: args.squadId,
      name: args.name.trim() || 'New team',
      colorKey,
      memberIds: Array.from(new Set(args.memberIds || [])),
      archived: false,
      createdAt: now,
      updatedAt: now,
      createdBy: args.createdBy,
    };
    await setDoc(ref, payload);
    return ref.id;
  }

  static async renameTeam(squadId: string, teamId: string, name: string): Promise<void> {
    await updateDoc(teamRef(squadId, teamId), {
      name: name.trim() || 'Team',
      updatedAt: Date.now(),
    });
  }

  static async archiveTeam(squadId: string, teamId: string): Promise<void> {
    await updateDoc(teamRef(squadId, teamId), {
      archived: true,
      memberIds: [],
      updatedAt: Date.now(),
    });
  }

  /** Move a student onto a team (removes from any other active team in the squad). */
  static async assignStudent(args: {
    squadId: string;
    studentId: string;
    toTeamId: string | null;
    teams: ClassroomTeam[];
  }): Promise<void> {
    const { squadId, studentId, toTeamId, teams } = args;
    const batch = writeBatch(db);
    const active = teams.filter((t) => !t.archived);
    let writes = 0;

    for (const team of active) {
      const has = team.memberIds.includes(studentId);
      const shouldHave = toTeamId !== null && team.id === toTeamId;
      if (has === shouldHave) continue;
      const nextMembers = shouldHave
        ? Array.from(new Set([...team.memberIds, studentId]))
        : team.memberIds.filter((id) => id !== studentId);
      batch.update(teamRef(squadId, team.id), {
        memberIds: nextMembers,
        updatedAt: Date.now(),
      });
      writes += 1;
    }

    if (writes > 0) await batch.commit();
  }

  static async createTeamWithStudent(args: {
    squadId: string;
    name: string;
    studentId: string;
    existingTeams: ClassroomTeam[];
    createdBy?: string;
  }): Promise<string> {
    await this.assignStudent({
      squadId: args.squadId,
      studentId: args.studentId,
      toTeamId: null,
      teams: args.existingTeams,
    });
    return this.createTeam({
      squadId: args.squadId,
      name: args.name,
      memberIds: [args.studentId],
      existingTeams: args.existingTeams,
      createdBy: args.createdBy,
    });
  }
}
